import { useEffect, useMemo, useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Loader2, RefreshCw, FileSearch } from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const ACTION_STYLES = {
  create: 'bg-emerald-500',
  update: 'bg-blue-500',
  delete: 'bg-rose-500',
  replace: 'bg-amber-500',
  restore: 'bg-teal-500',
  bulk_insert: 'bg-purple-500',
  bulk_delete: 'bg-orange-500'
};

const COLLECTION_LABELS = {
  tasks: 'task',
  projects: 'progetto',
  macro_projects: 'macro progetto',
  resources: 'risorsa',
  users: 'utente',
  holidays: 'ferie',
  ai_insights: 'insight AI'
};

const formatDate = function(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return '-';
  return date.toLocaleString('it-IT');
};

const jsonPreview = function(value) {
  if (!value) return '';
  try {
    return JSON.stringify(value, null, 2);
  } catch (e) {
    return String(value);
  }
};

const formatChangeValue = function(value) {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch (e) {
    return String(value);
  }
};

const toChangesRows = function(changes) {
  if (!changes || typeof changes !== 'object') return [];
  return Object.keys(changes).sort().map(function(key) {
    const entry = changes[key] || {};
    return {
      field: key,
      oldValue: formatChangeValue(entry.old),
      newValue: formatChangeValue(entry.new)
    };
  });
};

const getEntityLabel = function(entry) {
  if (!entry) return '';
  if (entry.meta && entry.meta.entityName) return entry.meta.entityName;
  if (entry.meta && entry.meta.name) return entry.meta.name;
  return entry.entityId || '';
};

const summarizeChanges = function(changes) {
  const keys = changes ? Object.keys(changes).sort() : [];
  if (!keys.length) return '';
  const firstKey = keys[0];
  const first = changes[firstKey] || {};
  const oldValue = formatChangeValue(first.old);
  const newValue = formatChangeValue(first.new);
  const more = keys.length > 1 ? ` +${keys.length - 1}` : '';
  return `${firstKey} da ${oldValue} a ${newValue}${more}`;
};

const buildEntrySummary = function(entry) {
  if (!entry) return '';
  const actor = entry.actor || 'Utente';
  const collection = COLLECTION_LABELS[entry.collection] || (entry.collection ? entry.collection.replace(/_/g, ' ') : 'elemento');
  const entity = getEntityLabel(entry);
  if (entry.action === 'update' && entry.changes && Object.keys(entry.changes).length) {
    return `${actor} ha modificato ${collection} ${entity} cambiando ${summarizeChanges(entry.changes)}`;
  }
  if (entry.action === 'create') {
    return `${actor} ha creato ${collection} ${entity}`;
  }
  if (entry.action === 'delete') {
    return `${actor} ha eliminato ${collection} ${entity}`;
  }
  if (entry.action === 'replace') {
    return `${actor} ha sostituito ${collection} ${entity}`;
  }
  return `${actor} ha eseguito ${entry.action || 'azione'} su ${collection} ${entity}`;
};

export const JournalTab = ({ authAxios, apiUrl, canManageJournal = false }) => {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [collectionFilter, setCollectionFilter] = useState('all');
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const loadJournal = async function() {
    try {
      setLoading(true);
      const response = await authAxios.get(apiUrl + '/journal?limit=200');
      setEntries(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error loading journal:', error);
      toast.error('Errore nel caricamento del journal');
    } finally {
      setLoading(false);
    }
  };

  useEffect(function() {
    loadJournal();
  }, []);

  const actionOptions = useMemo(function() {
    const set = new Set(entries.map(function(e) { return e.action; }).filter(Boolean));
    return ['all'].concat(Array.from(set).sort());
  }, [entries]);

  const collectionOptions = useMemo(function() {
    const set = new Set(entries.map(function(e) { return e.collection; }).filter(Boolean));
    return ['all'].concat(Array.from(set).sort());
  }, [entries]);

  const filteredEntries = useMemo(function() {
    const searchValue = search.trim().toLowerCase();
    return entries.filter(function(entry) {
      if (actionFilter !== 'all' && entry.action !== actionFilter) return false;
      if (collectionFilter !== 'all' && entry.collection !== collectionFilter) return false;
      if (!searchValue) return true;
      const haystack = [
        entry.actor,
        entry.collection,
        entry.action,
        entry.entityId
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(searchValue);
    });
  }, [entries, search, actionFilter, collectionFilter]);

  const handleOpenDetails = function(entry) {
    setSelectedEntry(entry);
    setDetailsOpen(true);
  };
  const hasChanges = selectedEntry && selectedEntry.changes && Object.keys(selectedEntry.changes).length > 0;
  const changesRows = hasChanges ? toChangesRows(selectedEntry.changes) : [];
  const summaryText = selectedEntry ? buildEntrySummary(selectedEntry) : '';

  const handleDeleteAll = async function() {
    if (!window.confirm('Confermi la cancellazione di tutto il journal?')) return;
    try {
      const response = await authAxios.delete(apiUrl + '/journal');
      toast.success('Journal cancellato: ' + (response.data.deleted || 0));
      loadJournal();
    } catch (error) {
      console.error('Error deleting journal:', error);
      toast.error('Errore nella cancellazione del journal');
    }
  };

  const handleDeleteRange = async function() {
    if (!fromDate || !toDate) {
      toast.error('Seleziona entrambe le date');
      return;
    }
    if (!window.confirm('Confermi la cancellazione del journal nel range selezionato?')) return;
    try {
      const response = await authAxios.delete(apiUrl + '/journal/range', {
        params: { from: fromDate, to: toDate }
      });
      toast.success('Voci eliminate: ' + (response.data.deleted || 0));
      loadJournal();
    } catch (error) {
      console.error('Error deleting journal range:', error);
      toast.error('Errore nella cancellazione del range');
    }
  };

  const handleRestore = async function(entry) {
    if (!entry) return;
    if (!window.confirm('Confermi il ripristino di questa voce?')) return;
    try {
      await authAxios.post(apiUrl + '/journal/' + entry.id + '/restore');
      toast.success('Ripristino completato');
      loadJournal();
    } catch (error) {
      console.error('Error restoring journal entry:', error);
      if (error.response && error.response.data && error.response.data.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Errore nel ripristino');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Journal Attivita</h2>
          <p className="text-sm text-muted-foreground">Registro delle modifiche CRUD su database</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={loadJournal} variant="outline" data-testid="journal-refresh">
            <RefreshCw className="w-4 h-4 mr-2" />
            Aggiorna
          </Button>
          {canManageJournal && (
            <Button onClick={handleDeleteAll} variant="destructive" data-testid="journal-delete-all">
              Cancella tutto
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2">
          <FileSearch className="w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={function(e) { setSearch(e.target.value); }}
            placeholder="Cerca utente, azione, id..."
            className="h-9 w-56"
            data-testid="journal-search"
          />
        </div>
        <select
          value={actionFilter}
          onChange={function(e) { setActionFilter(e.target.value); }}
          className="h-9 px-2 text-xs rounded-md border border-input bg-background"
          data-testid="journal-action-filter"
        >
          {actionOptions.map(function(opt) {
            return (
              <option key={opt} value={opt}>
                {opt === 'all' ? 'Tutte le azioni' : opt}
              </option>
            );
          })}
        </select>
        <select
          value={collectionFilter}
          onChange={function(e) { setCollectionFilter(e.target.value); }}
          className="h-9 px-2 text-xs rounded-md border border-input bg-background"
          data-testid="journal-collection-filter"
        >
          {collectionOptions.map(function(opt) {
            return (
              <option key={opt} value={opt}>
                {opt === 'all' ? 'Tutte le collezioni' : opt}
              </option>
            );
          })}
        </select>
        {canManageJournal && (
          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={fromDate}
              onChange={function(e) { setFromDate(e.target.value); }}
              className="h-9 w-[150px]"
              data-testid="journal-range-from"
            />
            <Input
              type="date"
              value={toDate}
              onChange={function(e) { setToDate(e.target.value); }}
              className="h-9 w-[150px]"
              data-testid="journal-range-to"
            />
            <Button onClick={handleDeleteRange} variant="destructive" data-testid="journal-delete-range">
              Cancella range
            </Button>
          </div>
        )}
        <div className="text-xs text-muted-foreground ml-auto">
          Totale: {total}
        </div>
      </div>

      <div className="border border-border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Data</TableHead>
              <TableHead>Utente</TableHead>
              <TableHead>Azione</TableHead>
              <TableHead>Collezione</TableHead>
              <TableHead>ID</TableHead>
              <TableHead>Sintesi</TableHead>
              <TableHead className="w-[120px]">Dettagli</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredEntries.map(function(entry) {
              const actionClass = ACTION_STYLES[entry.action] || 'bg-slate-500';
              return (
                <TableRow key={entry.id}>
                  <TableCell className="text-xs text-muted-foreground">
                    {formatDate(entry.createdAt)}
                  </TableCell>
                  <TableCell className="font-medium">{entry.actor || '-'}</TableCell>
                  <TableCell>
                    <Badge className={cn('capitalize', actionClass)}>
                      {entry.action || 'n/a'}
                    </Badge>
                  </TableCell>
                  <TableCell>{entry.collection || '-'}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{entry.entityId || '-'}</TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-[320px] truncate">
                    {buildEntrySummary(entry)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={function() { handleOpenDetails(entry); }}
                        data-testid={'journal-details-' + entry.id}
                      >
                        Dettagli
                      </Button>
                      {canManageJournal && (entry.action === 'update' || entry.action === 'delete') && !(entry.action === 'delete' && entry.collection === 'users') && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={function() { handleRestore(entry); }}
                          data-testid={'journal-restore-' + entry.id}
                        >
                          Restore
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {filteredEntries.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                  Nessuna voce trovata
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="sm:max-w-[720px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Dettagli Journal</DialogTitle>
          </DialogHeader>
          {selectedEntry && (
            <div className="space-y-4">
              {summaryText && (
                <div className="text-sm bg-muted/40 border border-border rounded-md p-3">
                  {summaryText}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground">Data</p>
                  <p>{formatDate(selectedEntry.createdAt)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Utente</p>
                  <p>{selectedEntry.actor || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Azione</p>
                  <p>{selectedEntry.action || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Collezione</p>
                  <p>{selectedEntry.collection || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Entity ID</p>
                  <p>{selectedEntry.entityId || '-'}</p>
                </div>
              </div>

              {selectedEntry.meta && (
                <div>
                  <p className="text-sm font-medium mb-2">Meta</p>
                  <pre className="text-xs bg-muted/40 border border-border rounded-md p-3 max-h-48 overflow-auto">
                    {jsonPreview(selectedEntry.meta)}
                  </pre>
                </div>
              )}

              {hasChanges && (
                <div>
                  <p className="text-sm font-medium mb-2">Modifiche</p>
                  <div className="border border-border rounded-md overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Campo</TableHead>
                          <TableHead>Old value</TableHead>
                          <TableHead>New value</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {changesRows.map(function(row) {
                          return (
                            <TableRow key={row.field}>
                              <TableCell className="text-xs text-muted-foreground">{row.field}</TableCell>
                              <TableCell className="text-xs">{row.oldValue}</TableCell>
                              <TableCell className="text-xs">{row.newValue}</TableCell>
                            </TableRow>
                          );
                        })}
                        {changesRows.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={3} className="text-center text-muted-foreground text-xs py-6">
                              Nessuna modifica rilevata
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {!hasChanges && selectedEntry.before && (
                <div>
                  <p className="text-sm font-medium mb-2">Prima</p>
                  <pre className="text-xs bg-muted/40 border border-border rounded-md p-3 max-h-60 overflow-auto">
                    {jsonPreview(selectedEntry.before)}
                  </pre>
                </div>
              )}

              {!hasChanges && selectedEntry.after && (
                <div>
                  <p className="text-sm font-medium mb-2">Dopo</p>
                  <pre className="text-xs bg-muted/40 border border-border rounded-md p-3 max-h-60 overflow-auto">
                    {jsonPreview(selectedEntry.after)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JournalTab;
